import React, { useState } from 'react';
import { Contact } from '../../../../config/api';
import { useCoCounsel, CoCounsel, CreateCoCounselData } from '../../hooks/useCoCounsel';

interface CoCounselTabProps {
  contact: Contact;
}

const EMPTY_FORM: CreateCoCounselData = {
  name: '',
  firm: '',
  email: '',
  phone: '',
  role: 'associate',
  relationship: 'secondary',
  status: 'pending',
  start_date: new Date().toISOString().split('T')[0],
  end_date: '',
  fee_split: undefined,
  notes: '',
};

const inputClass = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-lex-accent';
const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

const CoCounselTab: React.FC<CoCounselTabProps> = ({ contact }) => {
  const {
    coCounsel,
    loading,
    error: hookError,
    total,
    feeSplitTotal,
    addCoCounsel,
    updateCoCounsel,
    removeCoCounsel,
  } = useCoCounsel(contact.id);

  const [selectedRelationship, setSelectedRelationship] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingCounsel, setEditingCounsel] = useState<CoCounsel | null>(null);
  const [formData, setFormData] = useState<CreateCoCounselData>({ ...EMPTY_FORM });
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const getRelationshipColor = (relationship: string) => {
    switch (relationship) {
      case 'primary':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
      case 'secondary':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'consulting':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'lead':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
      case 'associate':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'consultant':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300';
      case 'expert':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const filteredCoCounsel = coCounsel.filter(counsel => {
    const matchesRelationship = selectedRelationship === 'all' || counsel.relationship === selectedRelationship;
    const matchesStatus = selectedStatus === 'all' || counsel.status === selectedStatus;
    return matchesRelationship && matchesStatus;
  });

  const openAddModal = () => {
    setEditingCounsel(null);
    setFormData({ ...EMPTY_FORM });
    setFormError(null);
    setShowModal(true);
  };

  const openEditModal = (counsel: CoCounsel) => {
    setEditingCounsel(counsel);
    setFormData({
      name: counsel.name,
      firm: counsel.firm,
      email: counsel.email || '',
      phone: counsel.phone || '',
      role: counsel.role,
      relationship: counsel.relationship,
      status: counsel.status,
      start_date: counsel.startDate ? counsel.startDate.split('T')[0] : '',
      end_date: counsel.endDate ? counsel.endDate.split('T')[0] : '',
      fee_split: counsel.feeSplit,
      notes: counsel.notes || '',
    });
    setFormError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCounsel(null);
    setFormData({ ...EMPTY_FORM });
    setFormError(null);
  };

  const handleFieldChange = (field: keyof CreateCoCounselData, value: string | number | undefined) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setFormError(null);

    if (!formData.name.trim()) {
      setFormError('Name is required.');
      return;
    }
    if (!formData.firm.trim()) {
      setFormError('Firm is required.');
      return;
    }
    if (!formData.start_date) {
      setFormError('Start date is required.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        fee_split: formData.fee_split !== undefined && formData.fee_split !== null && String(formData.fee_split).trim() !== ''
          ? Number(formData.fee_split)
          : undefined,
        end_date: formData.end_date || undefined,
        notes: formData.notes || undefined,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
      };

      if (editingCounsel) {
        await updateCoCounsel(editingCounsel.id, payload);
      } else {
        await addCoCounsel(payload);
      }
      closeModal();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'An error occurred.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (counsel: CoCounsel) => {
    const confirmed = window.confirm(`Remove ${counsel.name} from co-counsel? This can be undone by an administrator.`);
    if (!confirmed) return;

    try {
      await removeCoCounsel(counsel.id);
    } catch {
      // Error is set on the hook
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Co-Counsel</h3>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent" />
          <span className="ml-3 text-gray-500 dark:text-gray-400">Loading co-counsel...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Co-Counsel</h3>

      {/* Error Banner */}
      {hookError && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
          {hookError}
        </div>
      )}

      {/* Team Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-lex-accent-soft rounded-lg p-4 w-full">
          <div className="text-sm text-lex-accent">Team Members</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{total}</div>
        </div>
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 w-full">
          <div className="text-sm text-green-600 dark:text-green-400">Total Fee Split</div>
          <div className="text-2xl font-bold text-green-900 dark:text-green-100">{feeSplitTotal}%</div>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="flex flex-col sm:flex-row gap-4">
        <select
          value={selectedRelationship}
          onChange={(e) => setSelectedRelationship(e.target.value)}
          className={inputClass}
          style={{ width: 'auto' }}
        >
          <option value="all">All Relationships</option>
          <option value="primary">Primary</option>
          <option value="secondary">Secondary</option>
          <option value="consulting">Consulting</option>
        </select>

        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className={inputClass}
          style={{ width: 'auto' }}
        >
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="pending">Pending</option>
        </select>

        <button
          onClick={openAddModal}
          className="px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover transition-colors"
        >
          Add Co-Counsel
        </button>
      </div>

      {/* Team Members */}
      <div className="space-y-4">
        <h4 className="text-lg font-medium text-gray-900 dark:text-white">Team Members</h4>
        {filteredCoCounsel.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            {coCounsel.length === 0
              ? 'No co-counsel assigned yet. Click "Add Co-Counsel" to get started.'
              : 'No co-counsel match the selected filters.'}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredCoCounsel.map((counsel) => (
              <div
                key={counsel.id}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-lex-accent transition-colors cursor-pointer"
                onClick={() => openEditModal(counsel)}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-x-4 gap-y-1">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-lex-accent-soft rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-lex-accent font-semibold">
                        {counsel.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white break-words">{counsel.name}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 break-words">{counsel.firm}</div>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 mt-2 sm:mt-0">
                    <span className={`px-2 py-1 text-xs rounded-full capitalize ${getRoleColor(counsel.role)}`}>{counsel.role}</span>
                    <span className={`px-2 py-1 text-xs rounded-full capitalize ${getRelationshipColor(counsel.relationship)}`}>{counsel.relationship}</span>
                    <span className={`px-2 py-1 text-xs rounded-full capitalize ${getStatusColor(counsel.status)}`}>{counsel.status}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleRemove(counsel); }}
                      className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                  <div className="flex flex-col sm:flex-row gap-x-4 gap-y-1">
                    {counsel.email && <span>Email: {counsel.email}</span>}
                    {counsel.phone && <span>Phone: {counsel.phone}</span>}
                    {counsel.feeSplit != null && <span>Fee Split: {counsel.feeSplit}%</span>}
                  </div>
                  {counsel.notes && (
                    <div className="text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{counsel.notes}</div>
                  )}
                  <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    Started: {new Date(counsel.startDate).toLocaleDateString()}
                    {counsel.endDate && <> &middot; Ended: {new Date(counsel.endDate).toLocaleDateString()}</>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Co-Counsel Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                {editingCounsel ? 'Edit Co-Counsel' : 'Add Co-Counsel'}
              </h3>
              <button
                onClick={closeModal}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Error */}
              {formError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
                  {formError}
                </div>
              )}

              {/* Row 1: Name + Firm */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    className={inputClass}
                    placeholder="Attorney name"
                  />
                </div>
                <div>
                  <label className={labelClass}>Firm *</label>
                  <input
                    type="text"
                    value={formData.firm}
                    onChange={(e) => handleFieldChange('firm', e.target.value)}
                    className={inputClass}
                    placeholder="Law firm name"
                  />
                </div>
              </div>

              {/* Row 2: Email + Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Email</label>
                  <input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => handleFieldChange('email', e.target.value)}
                    className={inputClass}
                    placeholder="attorney@firm.com"
                  />
                </div>
                <div>
                  <label className={labelClass}>Phone</label>
                  <input
                    type="tel"
                    value={formData.phone || ''}
                    onChange={(e) => handleFieldChange('phone', e.target.value)}
                    className={inputClass}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              {/* Row 3: Role + Relationship */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Role</label>
                  <select
                    value={formData.role || 'associate'}
                    onChange={(e) => handleFieldChange('role', e.target.value)}
                    className={inputClass}
                  >
                    <option value="lead">Lead</option>
                    <option value="associate">Associate</option>
                    <option value="consultant">Consultant</option>
                    <option value="expert">Expert</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Relationship</label>
                  <select
                    value={formData.relationship || 'secondary'}
                    onChange={(e) => handleFieldChange('relationship', e.target.value)}
                    className={inputClass}
                  >
                    <option value="primary">Primary</option>
                    <option value="secondary">Secondary</option>
                    <option value="consulting">Consulting</option>
                  </select>
                </div>
              </div>

              {/* Row 4: Status + Fee Split */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Status</label>
                  <select
                    value={formData.status || 'pending'}
                    onChange={(e) => handleFieldChange('status', e.target.value)}
                    className={inputClass}
                  >
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Fee Split (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={formData.fee_split !== undefined && formData.fee_split !== null ? formData.fee_split : ''}
                    onChange={(e) => handleFieldChange('fee_split', e.target.value === '' ? undefined : Number(e.target.value))}
                    className={inputClass}
                    placeholder="0 - 100"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Active total: {feeSplitTotal}% &middot; Remaining: {Math.max(0, 100 - feeSplitTotal).toFixed(2)}%
                  </p>
                </div>
              </div>

              {/* Row 5: Start Date + End Date */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Start Date *</label>
                  <input
                    type="date"
                    value={formData.start_date || ''}
                    onChange={(e) => handleFieldChange('start_date', e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>End Date</label>
                  <input
                    type="date"
                    value={formData.end_date || ''}
                    onChange={(e) => handleFieldChange('end_date', e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Row 6: Notes */}
              <div>
                <label className={labelClass}>Notes</label>
                <textarea
                  value={formData.notes || ''}
                  onChange={(e) => handleFieldChange('notes', e.target.value)}
                  className={inputClass}
                  rows={3}
                  placeholder="Engagement details, specializations, billing notes..."
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-2 pt-2">
                <button
                  onClick={closeModal}
                  disabled={submitting}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover transition-colors disabled:opacity-50"
                >
                  {submitting
                    ? (editingCounsel ? 'Saving...' : 'Adding...')
                    : (editingCounsel ? 'Save Changes' : 'Add Co-Counsel')
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoCounselTab;
